/**
 * The grey "this source has data" tick.
 *
 * Deliberately understated — it matches the row it sits on rather than shouting
 * in green, because in the sandbox every source is seeded and a green check
 * would read as "authenticated". The status/count/freshness detail lives in the
 * hover tooltip, and is mirrored into `title` + `aria-label` so it stays
 * reachable by keyboard, screen reader, and touch long-press.
 */
import { Check } from "lucide-react";

interface SourceTickProps {
	/** Accessible name of the thing this tick describes, e.g. "Slack". */
	label: string;
	/** "Demo snapshot · synced 2h ago". */
	detail: string;
}

export default function SourceTick({ label, detail }: SourceTickProps) {
	return (
		<span
			className="group/tick relative flex-shrink-0 outline-none"
			tabIndex={0}
			title={detail}
			aria-label={`${label}: ${detail}`}
		>
			<Check
				className="h-4 w-4 text-gray-400 transition-colors group-hover/tick:text-gray-600 group-focus/tick:text-gray-600"
				strokeWidth={2.5}
				aria-hidden
			/>
			{/*
			 * Anchored to the LEFT of the tick and vertically centred on it, so the
			 * tooltip never leaves its row's box. Placing it above (`bottom-full`)
			 * put it outside the scrolling list, whose `overflow-y-auto` clips
			 * absolutely-positioned children — the top row's tooltip was invisible.
			 */}
			<span
				role="tooltip"
				className="pointer-events-none absolute right-full top-1/2 z-20 mr-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover/tick:block group-focus/tick:block"
			>
				{detail}
			</span>
		</span>
	);
}
